// Iter46-B — Admin → Incident Templates (versioned CRUD).
// Platform_admin can create global templates (tenant_id=null) and per-tenant.
// Tenant_admin can only create/edit/delete templates for their own tenant.
import React, { useState } from "react";
import { Plus, Edit2, Trash2, FileText, X, Check } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useStore } from "../store";
import {
  useIncidentTemplates,
  useCreateIncidentTemplate,
  useUpdateIncidentTemplate,
  useDeleteIncidentTemplate,
  useTenants,
} from "../api/sitroom";

const SEVERITIES = ["s1", "s2", "s3", "s4"];
const SLA_TIERS = ["standard", "fast", "critical"];
const PARTY_TYPES = ["OEM", "T1", "T2", "FF", "AC", "JC", "WH", "CB", "QP"];
const MANDATORY_FIELD_OPTIONS = [
  "part_number", "lot_number", "quantity_affected", "production_line",
  "vehicle_model", "customer_complaint", "containment_action", "root_cause",
  "carrier_name", "shipment_id", "delivery_date", "rework_scope",
];

const mono = (size, color, weight, ls) => ({
  fontFamily: "DM Mono, monospace",
  fontSize: size,
  color: color || "var(--text, #f0f2f8)",
  fontWeight: weight || 400,
  letterSpacing: ls,
});

const Label = ({ children }) => (
  <div className="mb-1" style={mono(9, "var(--dimmer, #9198b0)", 700, "0.8px")}>
    <span style={{ textTransform: "uppercase" }}>{children}</span>
  </div>
);

const inputStyle = {
  background: "var(--bg-base, #08090d)",
  border: "1px solid rgba(255,255,255,0.10)",
  color: "var(--text, #f0f2f8)",
  ...mono(11),
  padding: "8px 10px",
  borderRadius: 6,
  width: "100%",
  outline: "none",
};

export default function AdminIncidentTemplates() {
  const { user } = useAuth();
  const addToast = useStore((s) => s.addToast);
  const { data: templates = [], isLoading } = useIncidentTemplates();
  const { data: tenants = [] } = useTenants();
  const createMut = useCreateIncidentTemplate();
  const updateMut = useUpdateIncidentTemplate();
  const deleteMut = useDeleteIncidentTemplate();

  const [editing, setEditing] = useState(null); // null | "new" | <template_key>
  const isPlatform = user?.role === "platform_admin";

  const handleSave = async (body, existing) => {
    try {
      if (existing) {
        await updateMut.mutateAsync({ template_key: existing.template_key, body });
        addToast({ type: "success", title: "Template updated", body: `${body.name} v${existing.version + 1}` });
      } else {
        await createMut.mutateAsync(body);
        addToast({ type: "success", title: "Template created", body: body.name });
      }
      setEditing(null);
    } catch (e) {
      addToast({ type: "error", title: "Save failed", body: e?.response?.data?.detail || e.message });
    }
  };

  const handleDelete = async (tpl) => {
    if (!window.confirm(`Delete template "${tpl.name}" and all its versions?`)) return;
    try {
      await deleteMut.mutateAsync(tpl.template_key);
      addToast({ type: "success", title: "Template deleted", body: tpl.name });
    } catch (e) {
      addToast({ type: "error", title: "Delete failed", body: e?.response?.data?.detail || e.message });
    }
  };

  return (
    <div data-testid="admin-incident-templates">
      <div className="flex items-center justify-between mb-3">
        <div style={mono(10, "var(--dimmer, #9198b0)", 700, "0.8px")} className="uppercase">
          Incident templates · {templates.length}
        </div>
        <button
          type="button"
          onClick={() => setEditing("new")}
          className="px-3 py-1.5 rounded inline-flex items-center gap-1.5"
          style={{ background: "#185FA5", color: "#fff", ...mono(10, undefined, 700, "0.4px"), border: "none", cursor: "pointer" }}
          data-testid="template-new-btn"
        >
          <Plus size={12} /> Add template
        </button>
      </div>

      {isLoading && (
        <div className="px-3 py-6 text-center" style={mono(11, "var(--dimmer, #9198b0)")}>Loading templates…</div>
      )}

      {!isLoading && templates.length === 0 && editing !== "new" ? (
        <div className="rounded-md p-4" style={{ background: "var(--bg-surface, #0d0f18)", border: "1px solid rgba(255,255,255,0.10)" }} data-testid="template-empty">
          <div style={mono(11, "var(--dimmer, #9198b0)")}>
            No incident templates configured yet. Create one to pre-fill severity, SLA tier, mandatory fields, and auto-linked parties on the new-incident flow.
          </div>
        </div>
      ) : null}

      <div className="space-y-3">
        {templates.map((tpl) => (
          <TemplateCard
            key={tpl.template_key}
            tpl={tpl}
            tenants={tenants}
            isPlatform={isPlatform}
            userTenant={user?.tenant_id}
            editing={editing === tpl.template_key}
            onEdit={() => setEditing(tpl.template_key)}
            onCancel={() => setEditing(null)}
            onSave={(body) => handleSave(body, tpl)}
            onDelete={() => handleDelete(tpl)}
          />
        ))}
        {editing === "new" && (
          <TemplateCard
            tpl={null}
            tenants={tenants}
            isPlatform={isPlatform}
            userTenant={user?.tenant_id}
            editing={true}
            onCancel={() => setEditing(null)}
            onSave={(body) => handleSave(body, null)}
          />
        )}
      </div>
    </div>
  );
}

const TemplateCard = ({ tpl, tenants, isPlatform, userTenant, editing, onEdit, onCancel, onSave, onDelete }) => {
  const [name, setName] = useState(tpl?.name || "");
  const [severity, setSeverity] = useState(tpl?.default_severity || "s2");
  const [slaTier, setSlaTier] = useState(tpl?.default_sla_tier || "standard");
  const [parties, setParties] = useState(tpl?.auto_linked_party_types || []);
  const [fields, setFields] = useState(tpl?.mandatory_fields || []);
  const [tenantId, setTenantId] = useState(tpl?.tenant_id ?? (isPlatform ? null : userTenant));
  const [description, setDescription] = useState(tpl?.description || "");

  const toggle = (arr, val, setter) => {
    setter(arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val]);
  };

  const isGlobal = tpl?.tenant_id == null;
  const canEdit = isPlatform || !isGlobal;
  const tenantLabel = tpl?.tenant_id ? (tenants.find((t) => t.id === tpl.tenant_id)?.name || tpl.tenant_id) : "Global";

  const handleSubmit = (e) => {
    e?.preventDefault?.();
    if (!name.trim()) {
      return;
    }
    onSave({
      name: name.trim(),
      default_severity: severity,
      default_sla_tier: slaTier,
      auto_linked_party_types: parties,
      mandatory_fields: fields,
      description: description.trim() || null,
      tenant_id: tenantId || null,
    });
  };

  if (editing) {
    return (
      <form
        onSubmit={handleSubmit}
        className="rounded-md p-4"
        style={{ background: "var(--bg-surface, #0d0f18)", border: "1px solid rgba(24,95,165,0.5)" }}
        data-testid={tpl ? `template-edit-${tpl.template_key}` : "template-edit-new"}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
          <div>
            <Label>Name *</Label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Quality Lock — T1"
              style={inputStyle}
              data-testid="template-edit-name"
              required
            />
          </div>
          <div>
            <Label>Scope</Label>
            {isPlatform ? (
              <select
                value={tenantId || ""}
                onChange={(e) => setTenantId(e.target.value || null)}
                style={inputStyle}
                data-testid="template-edit-tenant"
              >
                <option value="">— Global (all tenants) —</option>
                {tenants.map((t) => (
                  <option key={t.id} value={t.id}>{t.name || t.id}</option>
                ))}
              </select>
            ) : (
              <input type="text" value={userTenant || ""} disabled style={{ ...inputStyle, opacity: 0.55 }} />
            )}
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
          <div>
            <Label>Default severity</Label>
            <select value={severity} onChange={(e) => setSeverity(e.target.value)} style={inputStyle} data-testid="template-edit-severity">
              {SEVERITIES.map((s) => <option key={s} value={s}>{s.toUpperCase()}</option>)}
            </select>
          </div>
          <div>
            <Label>Default SLA tier</Label>
            <select value={slaTier} onChange={(e) => setSlaTier(e.target.value)} style={inputStyle} data-testid="template-edit-sla">
              {SLA_TIERS.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
        </div>
        <div className="mb-3">
          <Label>Auto-linked party types</Label>
          <div className="flex flex-wrap gap-1.5">
            {PARTY_TYPES.map((p) => {
              const on = parties.includes(p);
              return (
                <button
                  key={p}
                  type="button"
                  onClick={() => toggle(parties, p, setParties)}
                  className="px-2 py-1 rounded"
                  style={{
                    background: on ? "rgba(24,95,165,0.18)" : "transparent",
                    border: `1px solid ${on ? "rgba(24,95,165,0.55)" : "rgba(255,255,255,0.10)"}`,
                    color: on ? "#9ec3ec" : "var(--text-dim, #c5cad8)",
                    ...mono(9, undefined, 700, "0.4px"),
                    textTransform: "uppercase",
                    cursor: "pointer",
                  }}
                  data-testid={`template-edit-party-${p}`}
                  aria-pressed={on}
                >{p}</button>
              );
            })}
          </div>
        </div>
        <div className="mb-3">
          <Label>Mandatory fields</Label>
          <div className="flex flex-wrap gap-1.5">
            {MANDATORY_FIELD_OPTIONS.map((f) => {
              const on = fields.includes(f);
              return (
                <button
                  key={f}
                  type="button"
                  onClick={() => toggle(fields, f, setFields)}
                  className="px-2 py-1 rounded"
                  style={{
                    background: on ? "rgba(243,177,74,0.15)" : "transparent",
                    border: `1px solid ${on ? "rgba(243,177,74,0.50)" : "rgba(255,255,255,0.10)"}`,
                    color: on ? "#f3b14a" : "var(--text-dim, #c5cad8)",
                    ...mono(9, undefined, 600, "0.3px"),
                    cursor: "pointer",
                  }}
                  data-testid={`template-edit-field-${f}`}
                  aria-pressed={on}
                >{f}</button>
              );
            })}
          </div>
        </div>
        <div className="mb-3">
          <Label>Description</Label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Internal note about when to use this template"
            style={{ ...inputStyle, minHeight: 60, resize: "vertical" }}
            data-testid="template-edit-description"
          />
        </div>
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onCancel}
            className="px-3 py-1.5 rounded"
            style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.14)", color: "var(--text-dim, #c5cad8)", ...mono(10, undefined, 600, "0.4px"), cursor: "pointer" }}
            data-testid="template-edit-cancel">Cancel</button>
          <button type="submit"
            className="px-3 py-1.5 rounded inline-flex items-center gap-1"
            style={{ background: "#185FA5", color: "#fff", ...mono(10, undefined, 700, "0.4px"), border: "none", cursor: "pointer" }}
            data-testid="template-edit-save"><Check size={11} /> {tpl ? "Save new version" : "Create"}</button>
        </div>
      </form>
    );
  }

  return (
    <div
      className="rounded-md p-4"
      style={{ background: "var(--bg-surface, #0d0f18)", border: "1px solid rgba(255,255,255,0.10)" }}
      data-testid={`template-card-${tpl.template_key}`}
    >
      <div className="flex items-start justify-between mb-2 gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <FileText size={15} color="#9ec3ec" />
          <span style={{ fontFamily: "Syne, sans-serif", fontWeight: 700, fontSize: 14, color: "var(--text, #f0f2f8)" }}>
            {tpl.name}
          </span>
          <span className="px-1.5 py-0.5 rounded"
            style={{ background: "rgba(24,95,165,0.18)", color: "#9ec3ec", ...mono(9, undefined, 700, "0.4px") }}>
            v{tpl.version}
          </span>
          <span className="px-1.5 py-0.5 rounded"
            style={{
              background: isGlobal ? "rgba(243,177,74,0.15)" : "rgba(78,208,132,0.10)",
              color: isGlobal ? "#f3b14a" : "#4ed084",
              ...mono(9, undefined, 700, "0.4px"),
            }}>
            {tenantLabel}
          </span>
        </div>
        <div className="flex gap-1">
          {canEdit && (
            <>
              <button type="button" onClick={onEdit}
                className="px-2 py-1 rounded inline-flex items-center gap-1"
                style={{ background: "transparent", color: "#9ec3ec", border: "1px solid rgba(24,95,165,0.40)", ...mono(9, undefined, 700, "0.4px"), cursor: "pointer" }}
                data-testid={`template-card-edit-${tpl.template_key}`}><Edit2 size={10} /> Edit</button>
              <button type="button" onClick={onDelete}
                className="px-2 py-1 rounded inline-flex items-center gap-1"
                style={{ background: "transparent", color: "#ff9a99", border: "1px solid rgba(226,75,74,0.40)", ...mono(9, undefined, 700, "0.4px"), cursor: "pointer" }}
                data-testid={`template-card-delete-${tpl.template_key}`}><Trash2 size={10} /> Del</button>
            </>
          )}
        </div>
      </div>
      <div className="flex flex-wrap gap-3 mb-2" style={mono(11, "var(--text-dim, #c5cad8)")}>
        <span>Severity <b style={{ color: "#9ec3ec" }}>{tpl.default_severity.toUpperCase()}</b></span>
        <span>SLA <b style={{ color: "#9ec3ec" }}>{tpl.default_sla_tier}</b></span>
        {tpl.auto_linked_party_types?.length > 0 && (
          <span>Parties <b style={{ color: "#f3b14a" }}>{tpl.auto_linked_party_types.join(", ")}</b></span>
        )}
        {tpl.mandatory_fields?.length > 0 && (
          <span>{tpl.mandatory_fields.length} mandatory field(s)</span>
        )}
      </div>
      {tpl.description && (
        <div style={mono(10, "var(--dimmer, #9198b0)")} className="mt-1">{tpl.description}</div>
      )}
    </div>
  );
};
